import { JSX, ParentComponent, For, Suspense } from 'solid-js';
import { animate } from 'motion/mini';
import { Ref } from '@solid-primitives/refs';
import { RouteDefinition } from '@solidjs/router';
import { createListTransition } from '@solid-primitives/transition-group';

import { cx } from '../../utils';
import Button from '../Button';
import { defaultKey, DialogConfig, StackProps, useRouterStacks } from './Context';
import { StaticRouter } from './StaticRouter';
import { animateOptions } from '../../libs/animateConfig';

function DialogContext(props: { route: RouteDefinition }) {
  const ctx = useRouterStacks();
  const stack = useStackByRoute(props.route);

  const state = () => {
    const stackList = ctx.stacks;
    const index = stackList.indexOf(stack());
    return {
      prev: stackList[index - 1],
      next: stackList[index + 1],
      action: ctx.action,
    };
  };

  const config = createMemo(() => {
    const matches = stack().matches.concat().reverse();
    for (const match of matches) {
      if (match.info?.dialog) {
        return match.info?.dialog;
      }
    }
    return {};
  });

  const active = createMemo(() => ctx.current === stack());

  const dialogCtx = {
    get active() {
      return active();
    },
    get stack() {
      return stack();
    },
    get config() {
      return config();
    },
    get prev() {
      return state().prev;
    },
    get next() {
      return state().next;
    },
  };

  return (
    <Context.Provider value={dialogCtx}>
      <ActiveProvider value={active()}>
        <Dialog />
      </ActiveProvider>
    </Context.Provider>
  );
}

function Dialog() {
  let dialog!: HTMLDivElement;
  const dialogCtx = useDialog();
  const transform = useStackTransform(0);
  createEffect(() => {
    const trans = transform();

    const cfg = dialogCtx.config;
    const zIndex =
      cfg.itemZIndex != null && cfg.itemZIndex !== ''
        ? String(cfg.itemZIndex)
        : trans[0] === 1 || trans[1] === 1
          ? '2'
          : '1';

    const style = dialog.style;
    style.zIndex = zIndex;
    style.display = 'block';
    animate(
      dialog,
      {
        transform: trans.map(covertTransform),
      },
      animateOptions
    ).then(() => {
      style.display = dialogCtx.active ? 'block' : 'none';
    });
  });

  return (
    <Ref ref={dialog}>
      <div class={cx('dialog-item', dialogCtx.config.class)}>
        <Suspense>
          <StaticRouter
            {...dialogCtx.stack}
            root={(props) => {
              return (
                <CustomErrorBoundary
                  fallback={(err, reset, defaultFallback) => {
                    const navigate = useNavigate();
                    const back = () => navigate(-1);
                    return (
                      <div class="w-full h-full center flex-col">
                        {defaultFallback}
                        <Closable>
                          <Button type="brand" onClick={back}>
                            close
                          </Button>
                        </Closable>
                      </div>
                    );
                  }}
                >
                  {props.children}
                </CustomErrorBoundary>
              );
            }}
          />
        </Suspense>
      </div>
    </Ref>
  );
}

type AneNumber = 0 | 1 | -1;
function covertTransform(an: AneNumber) {
  switch (an) {
    case 0:
      return 'translate3d(0, 0, 0)';
    case 1:
      return 'translate3d(100%, 0, 0)';
    case -1:
      return 'translate3d(-17%, 0, 0)';
    default:
      return 'translate3d(0, 0, 0)';
  }
}
function useStackTransform(index: number) {
  const ctx = useRouterStacks();
  const dialog = useDialog();
  // const currentMatchs = useCurrentMatches();
  const matches = dialog.stack.matches;

  const state = () => {
    const stackList = ctx.stacks;
    const index = stackList.indexOf(dialog.stack);
    return {
      prev: stackList[index - 1],
      next: stackList[index + 1],
      action: ctx.action,
    };
  };
  const active = createMemo(() => ctx.current === dialog.stack);
  return createMemo<[AneNumber, AneNumber]>(
    (pre) => {
      const isActive = active();
      const idle: [AneNumber, AneNumber] = [pre[1], pre[1]];
      const currentIsDialog = untrack(() => ctx.current.matches[index] !== matches[index]);
      if (currentIsDialog) return idle;
      const { prev, next, action } = untrack(state);

      if (isActive) {
        if (action === 'PUSH') {
          return prev?.dialog ? [1, 0] : [0, 0];
        } else if (action === 'POP') {
          return next?.dialog ? [-1, 0] : [0, 0];
        }
      } else {
        if (action === 'PUSH') {
          return next?.dialog ? [0, -1] : idle;
        } else if (action === 'POP') {
          return prev?.dialog ? [0, 1] : idle;
        }
      }
      return idle;
    },
    [0, 0]
  );
}

export function RouteTransition(props: ParentProps) {
  const ctx = useRouterStacks();

  const childs = children(() => props.children).toArray;

  function transition(el: HTMLDivElement, transform: [AneNumber, AneNumber], done: () => void) {
    const zIndex = transform[0] === 1 || transform[1] === 1 ? '2' : '1';
    el.style.zIndex = zIndex;
    animate(
      el,
      {
        transform: transform.map(covertTransform),
      },
      animateOptions
    ).then(done);
  }

  const active = useIsActive();
  let act = active();
  createComputed(() => {
    if (!active()) {
      act = false;
    }
  });

  return createListTransition<any>(childs, {
    onChange({ added, removed, finishRemoved }) {
      untrack(() => {
        const action = ctx.action;
        const cact = active();
        const ane = !act && cact;
        act = cact;

        // 这里的条件判断始终有点问题，后面再看有没有更好的调度方案。！！
        if (ane) {
          return finishRemoved(removed);
        }
        added.forEach((el) => {
          queueMicrotask(() => {
            // if (!el.parentElement) return;
            if (action === 'PUSH') {
              return transition(el, [1, 0], () => {});
            } else if (action === 'POP') {
              return transition(el, [-1, 0], () => {});
            }
          });
        });

        removed.forEach((el) => {
          if (action === 'PUSH') {
            return transition(el, [0, -1], () => finishRemoved([el]));
          } else if (action === 'POP') {
            return transition(el, [0, 1], () => finishRemoved([el]));
          }
        });
      });
    },
  }) as unknown as JSX.Element;
}

export function DialogPC(props: { root: ParentComponent }) {
  const ctx = useRouterStacks();
  const defaultStack = useStackByKey();
  const active = createMemo(() => ctx.current === defaultStack());

  return (
    <>
      <ActiveProvider value={active()}>
        <StaticRouter {...defaultStack()} root={props.root} />
      </ActiveProvider>
      <DialogStack />
    </>
  );
}

function DialogStack() {
  const ctx = useRouterStacks();
  const show = createMemo(() => ctx.current.dialog);

  let list!: HTMLDivElement;
  let portalRoot!: HTMLElement;

  const lastConfig = createMemo<DialogConfig | undefined>((pre) => {
    if (!ctx.current.dialog) {
      return pre;
    }
    let config: DialogConfig | undefined;
    for (const match of [...ctx.current.matches].reverse()) {
      if (match.info?.dialog) {
        config = match.info?.dialog;
        break;
      }
    }
    return config;
  });
  const dialogRoutes = useDialogRoutes();

  createEffect(() => {
    const cfg = lastConfig();
    if (!portalRoot) return;
    portalRoot.className = cx('dialog-root', cfg?.portalClass);
  });

  return (
    <Portal
      ref={(el) => {
        portalRoot = el;
        portalRoot.className = cx('dialog-root', lastConfig()?.portalClass);
        const display = el.style.display;
        el.style.display = 'none';
        createRenderEffect<boolean>((pre) => {
          const isShow = show();
          if (pre === isShow) return pre;
          el.style.display = display;
          animate(el, {
            backgroundColor: isShow ? '#000a' : '#0000',
            display: isShow ? display : 'none',
          });
          animate(
            list,
            {
              transform: `scale(${isShow ? 1 : 0.95})`,
              opacity: isShow ? 1 : 0,
            },
            animateOptions
          );
          return isShow;
        }, false);
      }}
    >
      <div ref={list} class={cx('dialog-list', lastConfig()?.class)}>
        <For each={dialogRoutes()}>{(route) => <DialogContext route={route} />}</For>
      </div>
    </Portal>
  );
}

function useStackByKey(source?: StackProps) {
  const ctx = useRouterStacks();
  source = source || ctx.root;

  return createMemo<StackProps>(() => {
    const key = source.key;
    const stackList = ctx.stacks;
    for (let i = ctx.index; i >= 0; i--) {
      if (key === stackList[i].key) {
        //TODO: 当历史记录被写满，replace打开为dialog时会有bug
        return stackList[i];
      }
    }
    //TODO: 当历史记录被写满，replace打开为dialog时会有bug
    return source;
  });
}

function useStackByRoute(source: RouteDefinition) {
  const ctx = useRouterStacks();

  return createMemo<StackProps>((pre) => {
    return getValidMatch(ctx.current.matches) === source ? ctx.current : pre!;
  });
}

export function useDialogRoutes() {
  const ctx = useRouterStacks();
  const [routes, setRoutes] = createSignal<RouteDefinition[]>([]);

  const keys = createMemo<Set<RouteDefinition>>(
    (keys) => {
      const list = ctx.stacks;
      keys.clear();
      for (const stack of list) {
        if (stack.key === defaultKey) continue;
        const key = getValidMatch(stack.matches);

        keys.add(key);
      }
      return keys;
    },
    new Set<RouteDefinition>(),
    { equals: false }
  );

  createEffect(() => setRoutes([...keys().values()]));

  return routes;
}

/** 有效的路由，过滤掉布局路由，布局路由path等于"/" */
function getValidMatch(matches: RouteDefinition[]) {
  return matches.slice(1).find((match) => match.path !== '/')!;
}

const Context = createContext<DialogContext>(null as unknown as DialogContext);

// eslint-disable-next-line
interface DialogContext {
  active: boolean;
  stack: StackProps;
  config: DialogConfig;
  prev?: StackProps;
  next?: StackProps;
}

export function useDialog() {
  return useContext(Context);
}

// export function DialogActions(props: ParentProps) {
//   const ctx = useDialog();
//   return <Portal mount={ctx.actionEl}>{props.children}</Portal>;
// }
